<!-- Messages -->
<div class="form-message" *ngIf="errorMessage">
  <div class="form-message-error">
    {{ errorMessage }}
  </div>
</div>
<div class="form-message" *ngIf="successMessage">
  <div class="form-message-success">
    {{ successMessage }}
  </div>
</div>
<div class="w-full flex justify-center py-8" *ngIf="currentState === 'SignUpInProcess'">
  <div class="w-12 h-12 rounded-full border-2 border-white/20 border-t-indigo-400 animate-spin" role="status"
    aria-label="Loading"></div>
</div>

<!-- Sign Up Form -->
<div id="signup" class="w-full max-w-lg mx-auto px-4 pt-2 pb-8 pb-20"
  *ngIf="currentState === 'SignUpUserInfo' || currentState === 'SignUpFailed'">
  <div class="form-container min-h-0">
    <div class="form-header flex-shrink-0">
      <h2 class="form-title">Create your account</h2>
      <p class="form-subtitle">Fill in your details below to get started.</p>
    </div>
    <form class="signup-form-body flex flex-col min-h-0 p-6 md:p-8 pt-0 space-y-3" [formGroup]="signUpForm"
      (ngSubmit)="signUp()">
      <div class="signup-container flex flex-col gap-2 min-h-0">
        <!-- First Name before Last Name -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="form-field">
            <label for="firstName" class="form-label form-label-required">First Name</label>
            <div class="form-field-input-wrapper">
              <input id="firstName" type="text" formControlName="firstName" required class="form-input">
            </div>
          </div>
          <div class="form-field">
            <label for="lastName" class="form-label">Last Name</label>
            <div class="form-field-input-wrapper">
              <input id="lastName" type="text" formControlName="lastName" class="form-input">
            </div>
          </div>
        </div>

        <div class="form-field">
          <label for="password" class="form-label form-label-required">Password</label>
          <div class="form-field-input-wrapper">
            <p class="form-error mb-1" *ngIf="signUpForm.get('password')?.invalid && signUpForm.get('password')?.touched && signUpForm.get('password')?.errors?.['pattern']">
              Must contain at least one number, one uppercase and one lowercase letter, and at least 8 characters.
            </p>
            <div class="relative">
              <input
                id="password"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="password"
                required
                class="form-input pr-10">
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-slate-300 hover:text-slate-100 focus:outline-none password-toggle-btn"
                (click)="showPassword = !showPassword"
                [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'">
                <i
                  class="fa-solid fa-fw text-sm"
                  [ngClass]="showPassword ? 'fa-eye-slash' : 'fa-eye'">
                </i>
              </button>
            </div>
          </div>
        </div>

        <div class="form-field">
          <label for="rePassword" class="form-label form-label-required">Re-enter Password</label>
          <div class="form-field-input-wrapper">
            <p class="form-error mb-1" *ngIf="signUpForm.get('rePassword')?.invalid && signUpForm.get('rePassword')?.touched && signUpForm.get('rePassword')?.errors?.['pattern']">
              Must contain at least one number, one uppercase and one lowercase letter, and at least 8 characters.
            </p>
            <div class="relative">
              <input
                id="rePassword"
                [type]="showPassword ? 'text' : 'password'"
                formControlName="rePassword"
                required
                class="form-input pr-10">
              <button
                type="button"
                class="absolute inset-y-0 right-0 flex items-center px-3 text-slate-300 hover:text-slate-100 focus:outline-none password-toggle-btn"
                (click)="showPassword = !showPassword"
                [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'">
                <i
                  class="fa-solid fa-fw text-sm"
                  [ngClass]="showPassword ? 'fa-eye-slash' : 'fa-eye'">
                </i>
              </button>
            </div>
          </div>
        </div>

        <div class="border-t border-white/10 pt-3 mt-0">
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Contact information</p>
          <div class="flex flex-col gap-2">
            <div class="form-field">
              <label for="email" class="form-label form-label-required">Email</label>
              <p *ngIf="emailLocked" class="text-xs text-emerald-300/90 mt-1 mb-1">
                Email recovered from your payment. You'll sign in with this email.
              </p>
              <p *ngIf="sessionParam && !emailLocked" class="text-xs text-amber-300/90 mt-1 mb-1">
                Please use the email address you used for payment.
              </p>
              <div class="form-field-input-wrapper">
                <input id="email" type="email" formControlName="email" required class="form-input"
                  [readonly]="emailLocked" [class.opacity-70]="emailLocked" [class.cursor-not-allowed]="emailLocked">
              </div>
            </div>
            <div class="form-field">
              <label for="phoneNumber" class="form-label form-label-required">Phone Number</label>
              <div class="form-field-input-wrapper">
                <p
                  class="form-error mb-1"
                  *ngIf="signUpForm.get('phoneNumber')?.invalid && signUpForm.get('phoneNumber')?.touched">
                  <span *ngIf="signUpForm.get('phoneNumber')?.errors?.['required']">
                    Phone number is required.
                  </span>
                  <span *ngIf="signUpForm.get('phoneNumber')?.errors?.['phoneNumber']">
                    Please enter a valid US phone number.
                  </span>
                </p>
                <input
                  id="phoneNumber"
                  type="tel"
                  formControlName="phoneNumber"
                  required
                  class="form-input"
                  maxlength="14"
                  placeholder="(555) 555-5555"
                  (input)="formatPhoneInput($event)">
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-white/10 pt-3 mt-1">
          <div class="form-field">
            <label for="licenseInState" class="form-label form-label-required">License In State</label>
            <div class="form-field-input-wrapper">
              <select id="licenseInState" formControlName="licenseInState" required class="form-select">
                <option value="">Choose your License In State</option>
                <option value="AL">Alabama</option>
                <option value="AK">Alaska</option>
                <option value="AZ">Arizona</option>
                <option value="AR">Arkansas</option>
                <option value="CA">California</option>
                <option value="CO">Colorado</option>
                <option value="CT">Connecticut</option>
                <option value="DE">Delaware</option>
                <option value="FL">Florida</option>
                <option value="GA">Georgia</option>
                <option value="HI">Hawaii</option>
                <option value="ID">Idaho</option>
                <option value="IL">Illinois</option>
                <option value="IN">Indiana</option>
                <option value="IA">Iowa</option>
                <option value="KS">Kansas</option>
                <option value="KY">Kentucky</option>
                <option value="LA">Louisiana</option>
                <option value="ME">Maine</option>
                <option value="MD">Maryland</option>
                <option value="MA">Massachusetts</option>
                <option value="MI">Michigan</option>
                <option value="MN">Minnesota</option>
                <option value="MS">Mississippi</option>
                <option value="MO">Missouri</option>
                <option value="MT">Montana</option>
                <option value="NE">Nebraska</option>
                <option value="NV">Nevada</option>
                <option value="NH">New Hampshire</option>
                <option value="NJ">New Jersey</option>
                <option value="NM">New Mexico</option>
                <option value="NY">New York</option>
                <option value="NC">North Carolina</option>
                <option value="ND">North Dakota</option>
                <option value="OH">Ohio</option>
                <option value="OK">Oklahoma</option>
                <option value="OR">Oregon</option>
                <option value="PA">Pennsylvania</option>
                <option value="RI">Rhode Island</option>
                <option value="SC">South Carolina</option>
                <option value="SD">South Dakota</option>
                <option value="TN">Tennessee</option>
                <option value="TX">Texas</option>
                <option value="UT">Utah</option>
                <option value="VT">Vermont</option>
                <option value="VA">Virginia</option>
                <option value="WA">Washington</option>
                <option value="WV">West Virginia</option>
                <option value="WI">Wisconsin</option>
                <option value="WY">Wyoming</option>
              </select>
            </div>
          </div>

          <div class="mt-4 rounded-lg bg-white/5 border border-white/10 overflow-hidden shadow-sm p-6 text-base text-slate-300">
            <label class="flex items-start gap-3 cursor-pointer hover:bg-white/5 transition-colors block -m-2 p-2 rounded">
              <input type="checkbox" formControlName="agreeToTerms" class="signup-checkbox shrink-0 w-4 h-4 mt-0.5 rounded border-white/30 bg-[#0f1629] text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0">
              <span class="min-w-0 leading-relaxed break-words">I agree to the following:</span>
            </label>
            <a href="javascript:void(0)" role="button" (click)="$event.preventDefault(); $event.stopPropagation(); openDialog()" class="inline-block mt-1 font-semibold text-indigo-400 hover:text-indigo-300 underline underline-offset-2">Enrollment Agreement</a>
          </div>
        </div>
        <div class="flex flex-col gap-4 mt-6 pt-5 border-t border-white/10 flex-shrink-0">
          <button type="submit" class="form-button form-button-primary w-full" [disabled]="!signUpForm.valid">Sign Up</button>
          <p class="text-center">
            <a class="text-sm font-semibold text-indigo-400 hover:text-indigo-300 cursor-pointer"
              (click)="triggerSignin.emit()">Back to Sign In</a>
          </p>
        </div>
      </div>
    </form>
  </div>
</div>

<!-- Confirm Sign Up -->
<div id="confirmSignUp" class="w-full max-w-lg mx-auto px-4 pt-2 pb-8 pb-20" *ngIf="currentState === 'ConfirmSignUp'">
  <div class="form-container min-h-0">
    <div class="form-header">
      <h2 class="form-title">Verify your email</h2>
      <p class="form-subtitle">Enter the code we sent to your email.</p>
    </div>
    <form class="form-body space-y-3" [formGroup]="confirmSignUpForm" (ngSubmit)="confirmSignUp()">
      <div class="signup-container flex flex-col gap-2">
        <!-- Username is derived from email and submitted automatically; field hidden -->
        <input type="hidden" formControlName="username">
        <div class="form-field">
          <label for="verificationCode" class="form-label form-label-required">Verification Code</label>
          <div class="form-field-input-wrapper">
            <input id="verificationCode" type="text" formControlName="verificationCode" class="form-input">
          </div>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-white/10">
        <button
          class="flex-1 h-10 min-h-[40px] rounded-lg font-medium border border-white/25 text-slate-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
          type="button" (click)="resendCode()">Resend Code</button>
        <button type="submit" class="form-button form-button-primary flex-1" [disabled]="!confirmSignUpForm.valid">Complete Signup</button>
      </div>
    </form>
  </div>
</div>

<!-- Sign Up Complete -->
<div id="signUpComplete" class="w-full max-w-lg mx-auto px-4 py-4 pb-8" *ngIf="currentState === 'SignUpComplete'">
  <div class="form-container">
    <div class="form-header">
      <h2 class="form-title">You're all set</h2>
      <p class="form-subtitle">Your account has been created successfully.</p>
    </div>
    <div class="p-6 md:p-8">
      <p class="text-slate-300">You have successfully signed up. Kindly sign in using your email and password.</p>
      <a class="inline-block mt-4 text-sm font-semibold text-indigo-400 hover:text-indigo-300 cursor-pointer"
        (click)="triggerSignin.emit()">Go to Sign In →</a>
    </div>
  </div>
</div>
